import React from "react";
import { COMPANY, BADGES } from "../data/site";
import { Phone, Download, MessageCircle, ArrowRight, ShieldCheck, ChevronDown } from "lucide-react";
import RotatingStamp from "./RotatingStamp";

const Hero = () => {
  return (
    <section
      id="home"
      className="relative hero-bg text-white overflow-hidden pt-36 pb-24 grain"
      data-testid="hero-section"
    >
      <div className="spotlight w-[600px] h-[600px] bg-orange-500 -top-40 -right-20" />
      <div className="spotlight w-[500px] h-[500px] bg-red-700 top-1/3 -left-32" />

      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      {/* Rotating stamp - top right corner */}
      <div className="hidden md:block absolute top-28 right-6 lg:right-12 z-10" data-testid="hero-stamp-wrap">
        <RotatingStamp size={170} />
      </div>

      <div className="max-w-6xl mx-auto px-6 text-center relative">
        <div className="inline-flex items-center gap-3 mb-9 fade-in">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-400" />
          </span>
          <div
            data-testid="rebrand-pill"
            className="px-4 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur text-amber-200 text-[11px] font-semibold tracking-[0.25em] uppercase font-mono"
          >
            Rebrand · Since {COMPANY.changeYear}
          </div>
        </div>

        <h1
          className="font-display font-extrabold leading-[0.95] mb-7 fade-in"
          style={{ animationDelay: "0.05s" }}
        >
          <span className="block text-white/40 text-2xl sm:text-3xl lg:text-4xl mb-3 line-through decoration-red-500/70 decoration-[3px]">
            ESS BEE ELECTRICAL
          </span>
          <span className="block text-white/70 text-3xl sm:text-4xl lg:text-5xl font-light">
            is now
          </span>
          <span className="block text-heat text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-3">
            ESS BEE HEATERS
          </span>
          <span className="block text-white text-3xl sm:text-4xl lg:text-5xl mt-2">
            PVT. LTD.
          </span>
        </h1>

        <p
          className="text-amber-50/85 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed mb-3 fade-in"
          style={{ animationDelay: "0.15s" }}
        >
          We are pleased to announce that{" "}
          <strong className="text-white">ESS BEE ELECTRICAL</strong> has been officially updated to{" "}
          <strong className="text-white">ESS BEE HEATERS PRIVATE LIMITED</strong> since{" "}
          <strong className="text-orange-300">{COMPANY.changeYear}</strong>. Same trusted team, sharper identity.
        </p>
        <p
          className="text-amber-50/55 text-sm max-w-2xl mx-auto leading-relaxed mb-10 fade-in"
          style={{ animationDelay: "0.2s" }}
        >
          ISO 9001:2015 certified manufacturer of industrial heaters & heating elements — Tubular, Infrared, Cartridge, Mica Band, Immersion, and more. Trusted by 1000+ clients across India since {COMPANY.since}.
        </p>

        <div
          className="flex flex-wrap justify-center gap-3 mb-12 fade-in"
          style={{ animationDelay: "0.25s" }}
        >
          <a
            href={`tel:${COMPANY.contacts[0].phone}`}
            data-testid="hero-call-btn"
            className="group inline-flex items-center gap-2 heat-gradient px-7 py-4 rounded-full font-bold text-white shadow-2xl shadow-red-600/40 hover:scale-[1.03] transition-transform"
          >
            <Phone className="w-4 h-4" /> Call {COMPANY.contacts[0].phone}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href={`https://wa.me/${COMPANY.contacts[0].whatsapp}`}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-2 bg-white/8 backdrop-blur border border-white/15 px-7 py-4 rounded-full font-bold text-white hover:bg-white/15 transition-colors"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Enquiry
          </a>
          <a
            href={COMPANY.catalogueUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-catalogue-btn"
            className="inline-flex items-center gap-2 bg-white text-[#1a0a05] px-7 py-4 rounded-full font-bold hover:bg-amber-100 transition-colors"
          >
            <Download className="w-4 h-4" /> Catalogue
          </a>
        </div>

        <div
          className="inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-6 py-3 rounded-full bg-white/5 border border-white/10 backdrop-blur mb-14 fade-in"
          style={{ animationDelay: "0.3s" }}
        >
          <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-100/90 font-mono">
            <ShieldCheck className="w-3.5 h-3.5 text-orange-300" /> ISO 9001:2015
          </span>
          <span className="h-3 w-px bg-white/20" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-100/90 font-mono">RoHS · CE · MSME</span>
          <span className="h-3 w-px bg-white/20" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-100/90 font-mono">Since {COMPANY.since}</span>
          <span className="h-3 w-px bg-white/20" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-100/90 font-mono">Made in India</span>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto fade-in"
          style={{ animationDelay: "0.35s" }}
        >
          <Stat value={COMPANY.yearsExcellence} label="Years of Excellence" />
          <Stat value="1000+" label="Happy Clients" />
          <Stat value="30+" label="Heater Range" />
          <Stat value="100%" label="QA Tested" />
        </div>

        {/* Scroll indicator */}
        <div className="mt-14 flex flex-col items-center gap-1 text-amber-300/60 font-mono text-[10px] tracking-[0.3em] uppercase fade-in" style={{ animationDelay: "0.45s" }}>
          <span>Scroll</span>
          <ChevronDown className="w-4 h-4 bounce-y" />
        </div>
      </div>

      <div className="relative mt-20 border-y border-white/10 bg-black/40 backdrop-blur overflow-hidden">
        <div className="marquee-track flex gap-12 py-4 whitespace-nowrap w-max">
          {[...BADGES, ...BADGES].map((b, i) => (
            <div
              key={i}
              className="flex items-center gap-3 text-amber-100/95 font-bold uppercase tracking-[0.2em] text-xs font-mono"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
              {b}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div className="group relative bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-5 py-6 hover:border-orange-400/60 transition-colors overflow-hidden">
    <div className="absolute -bottom-10 -right-10 w-24 h-24 rounded-full bg-orange-500/20 blur-2xl group-hover:bg-orange-500/40 transition-colors" />
    <div className="font-display font-extrabold text-4xl kpi-num text-heat relative">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-50/65 mt-1.5 relative font-mono">{label}</div>
  </div>
);

export default Hero;
